import { Suspense } from "react";
import { PreEligibilityFunnel } from "./PreEligibilityFunnel";
import { extractBrandFromRequest, loadClientBrand } from "../../modules/branding";
import { loadTrustpilotScore } from "../../modules/trustpilot-score";
import { loadFeatureFlags } from "../../modules/feature-flags";
import { loadCoralCampaign } from "../../settings/coralCampaign";
import { loadUserProfile, useUser } from "../../modules/auth";
import { Await, Navigate, useAsyncError, useLoaderData, LoaderFunctionArgs } from "react-router";
import { FunnelProgressBar } from "v2/components/funnel/ui-elements/ProgressBar";
import { Content } from "v2/components/layout/Content";
import { PageAlertPortal, PageShell } from "v2/components/shells/PageShell";
import { useProgressBar } from "v2/modules/progress-bar/progressBarState";
import { intakeProgressBar } from "./state/useIntakeProgressBarSegment";
import { sendErrorReport } from "../../../utils/reporting";
import { PreEligibilityPageViewEventTracker } from "../../modules/analytics/PreEligibilityPageViewEventTracker";
import { PageLoadingSpinner } from "../../components/layout/PageLoadingSpinner";
import { loadOptimizely } from "../../../tracking/tools/optimizely";
import { translationInitV2 as loadTranslations } from "../../../i18n";
import { appSettingsSnapshot } from "../../settings";
import { useNavigation } from "../../../hooks/useNavigation";
import { loadItineraryFlightsFromQueryParams } from "./features/flightsFromQueryParams";
import { ReturningUserAlert } from "../../../components/alerts/ReturningUserAlert";

export function Component() {
  const { segments, activeSegmentIndex, segmentTitle } = useProgressBar(intakeProgressBar);

  const { funnelRequiredLoaders } = useLoaderData() as {
    funnelRequiredLoaders: Promise<void>;
    funnelOptionalLoaders: Promise<void>;
  };
  const { profile } = useUser();
  const { replaceTo } = useNavigation();

  const isUserBlocked = profile?.claimingBlocked;

  if (isUserBlocked) {
    replaceTo("/claims/error-blocked");
    return;
  }

  return (
    <Suspense fallback={<PageLoadingSpinner />}>
      <Await resolve={funnelRequiredLoaders} errorElement={<ErrorBoundary />}>
        <PageShell
          sidebarContent={
            <FunnelProgressBar
              segments={segments}
              activeSegment={activeSegmentIndex}
              segmentTitle={segmentTitle}
            />
          }
        >
          <Content>
            <PreEligibilityPageViewEventTracker>
              <PreEligibilityFunnel />
            </PreEligibilityPageViewEventTracker>
          </Content>
          <PageAlertPortal>
            <ReturningUserAlert variant="action" />
          </PageAlertPortal>
        </PageShell>
      </Await>
    </Suspense>
  );
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  const { locale } = appSettingsSnapshot();

  const funnelRequiredLoaders = Promise.all([
    loadClientBrand(
      extractBrandFromRequest({
        request,
        params,
      }),
    ),
    loadUserProfile(),
    loadFeatureFlags(),
    loadTranslations(locale),
    loadItineraryFlightsFromQueryParams(),
  ]);

  const funnelOptionalLoaders = Promise.all([
    loadOptimizely(),
    loadCoralCampaign(),
    loadTrustpilotScore({ locale }),
  ]);

  return {
    funnelRequiredLoaders,
    funnelOptionalLoaders,
  };
}

export function ErrorBoundary() {
  const error = useAsyncError();

  sendErrorReport(error, "Unhandled error at pre-eligibility funnel");
  return <Navigate to={"/claims/processing-error"} replace />;
}
